import axios, { AxiosInstance } from "axios";

type AuthenticationServiceProps = {
  baseService: AxiosInstance;
  auth: string;
  users: string;
};

const AuthenticationService = ({
  baseService,
  auth,
  users,
}: AuthenticationServiceProps) => ({
  // Used when entering email and password, used also after using 2FA
  login: async (email: string, password: string) => {
    if (!email || !password) throw new Error("Missing Parameters"); // If main inputs aren't present
    const payload = {
      email: email,
      password: password,
    };
    // Create the request
    const res = await axios
      .post(auth, payload)
      .then((res) => res)
      .catch((err) => err);
    return res;
  },

  // Used when logging out of the portal
  logout: async () => {
    // Clear session storage
    sessionStorage.clear();

    // Create the request (w/ authentication, so using BaseService here.)
    const res = baseService
      .post(auth + "/logout", undefined)
      .then((res) => res)
      .catch((err) => err);

    return res;
  },

  // Used when user credentials authenticated and mfa required
  mfaRequired: async (email: string, password: string, totp: string) => {
    if (!email || !password || !totp) throw new Error("Missing Parameters");
    // If main inputs aren't present
    const payload = {
      email: email,
      password: password,
      totp: totp,
    };
    // Create the request
    const res = axios
      .post(auth + "/verify-token", payload)
      .then((res) => res)
      .catch((err) => err);
    return res;
  },

  // Used when user login for the first time
  setupMfa: async (
    email: string,
    password: string,
    twoFactor: string,
    newPassword: string
  ) => {
    if (newPassword) {
      if (!email || !newPassword || !twoFactor)
        throw new Error("Missing Parameters"); // If main inputs aren't present
    } else {
      if (!email || !password || !twoFactor)
        throw new Error("Missing Parameters"); // If main inputs aren't present
    }

    // Create the request
    const res = axios
      .post(auth + "/set-Mfa", {
        email: email,
        password: newPassword ? newPassword : password,
        totp: twoFactor,
      })
      .then((res) => res)
      .catch((err) => err);

    return res;
  },

  // Used when user login for the first time
  changePassword: async (
    email: string,
    password: string,
    newPassword: string,
    registrationToken?: string
  ) => {
    // If the `registrationToken` is present, only send `newPassword`
    const payload = registrationToken
      ? { newPassword: newPassword }
      : {
          email: email,
          password: password,
          newPassword: newPassword,
        };

    // Create the request (if token is present, it means that user is registering)
    const res = axios
      .post(
        registrationToken
          ? auth + "/confirm-password?registration_token=" + registrationToken
          : auth + "/confirm-password",
        payload
      )
      .then((res) => res)
      .catch((err) => err);
    return res;
  },

  // Used when the logged in user changes their password
  changeMyPassword: async (password: string, newPassword: string) => {
    return baseService.post(auth + "/change-password", {
      previousPassword: password,
      newPassword: newPassword,
    });
  },

  // Used when registering a user (email link)
  register: async (registrationToken: string) => {
    if (!registrationToken)
      throw new Error("Missing Parameters (?registration_token)");

    return axios.get(
      users +
        "/validate/registration-token?registration_token=" +
        registrationToken
    );
  },

  // Used for fetching the Forgot Password RegEx
  getForgotPasswordRegEx: async () =>
    axios.get(auth + "/validation?operation=forgot-password"),

  // Used when submitting a Forgot Password request
  forgotPassword: async (email: string) => {
    if (!email) throw new Error("Missing Parameters (email: string)");

    const res = axios
      .post(auth + "/forgot-password", {
        email: email,
      })
      .then((res) => res)
      .catch((err) => err);

    return res;
  },

  // Used when submitting a Forgot Password request with new password
  confirmForgotPassword: async (
    email: string,
    code: string,
    newPassword: string
  ) => {
    if (!email || !code)
      throw new Error("Missing Parameters (email: string or code: string)");

    const res = axios
      .post(auth + `/confirm-reset-password?code=${code}`, {
        email,
        newPassword,
      })
      .then((res) => res)
      .catch((err) => err);

    return res;
  },
});

export default AuthenticationService;
